/*
 * Composer — the single input affordance at the bottom of the Orbit chat.
 *
 * Features:
 *   - Multi-line textarea, autogrows 1 → 6 rows then scrolls
 *   - Enter sends, Shift+Enter newline
 *   - Voice INPUT (record → transcribe → review → send): tap the mic to
 *     RECORD audio. A live, branded waveform rides the input's top border
 *     while recording. Tap stop → the clip is transcribed (Whisper) with a
 *     loading state → the transcript lands in the input for the user to
 *     review/edit and send. NOT live dictation — recorded, then transcribed.
 *   - Paperclip → file input (PDF / DOCX / MD / TXT / image) via /ingest-file
 *   - URL detection: when the input looks like a URL, the submit pill swaps
 *     to "scan site" so the user knows it routes to Firecrawl extract.
 *
 * The composer is "dumb" — it doesn't know about questions or dossier state.
 * It emits onSendText / onSendUrl / onUploadDoc.
 */

import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  ArrowUp,
  Mic,
  Square,
  Paperclip,
  Loader2,
  Link as LinkIcon,
} from 'lucide-react';

import { useVoiceRecorder } from './useVoiceRecorder';
import { VoiceWaveform } from './VoiceWaveform';


const URL_REGEX = /^(https?:\/\/|www\.)\S+\.[a-z]{2,}/i;


export interface ComposerProps {
  onSendText: (text: string) => Promise<void> | void;
  onSendUrl: (url: string) => Promise<void> | void;
  onUploadDoc: (file: File) => Promise<void> | void;
  // Legacy whisper-fallback hook — no longer used (voice records + transcribes
  // in-place now). Kept optional so existing call sites don't break.
  onUploadVoiceBlob?: (blob: Blob, filename: string) => Promise<void> | void;
  placeholder?: string;
  disabled?: boolean;
}


export function Composer({
  onSendText,
  onSendUrl,
  onUploadDoc,
  placeholder = 'Type, drop, or speak…',
  disabled = false,
}: ComposerProps) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Voice: record → transcribe → drop into the input. The transcript APPENDS
  // to whatever's already typed, then we focus so the user can review + send.
  const voice = useVoiceRecorder(
    useCallback((t: string) => {
      setText((prev) => (prev.trim() ? `${prev.trim()} ${t}` : t));
      requestAnimationFrame(() => textareaRef.current?.focus());
    }, []),
  );
  const recording = voice.status === 'recording';
  const transcribing = voice.status === 'transcribing';

  // Autogrow the textarea up to ~6 rows then scroll inside.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxH = 24 * 6 + 24; // ~6 lines × line-height + padding
    el.style.height = Math.min(el.scrollHeight, maxH) + 'px';
  }, [text]);

  const isUrl = URL_REGEX.test(text.trim());
  const busy = submitting || transcribing || recording;

  const submit = useCallback(async () => {
    const value = text.trim();
    if (!value || submitting || disabled || recording || transcribing) return;
    setText('');
    setSubmitting(true);
    try {
      if (isUrl) {
        await onSendUrl(value);
      } else {
        await onSendText(value);
      }
    } finally {
      setSubmitting(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [text, submitting, disabled, recording, transcribing, isUrl, onSendUrl, onSendText]);

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  const toggleVoice = useCallback(() => {
    if (recording) voice.stop();
    else if (!transcribing) void voice.start();
  }, [recording, transcribing, voice]);

  // ── File pick → upload ────────────────────────────────────────────
  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      setSubmitting(true);
      try {
        await onUploadDoc(file);
      } finally {
        setSubmitting(false);
      }
    },
    [onUploadDoc],
  );

  const textareaPlaceholder = recording
    ? 'Listening… tap stop when you’re done'
    : transcribing
      ? 'Transcribing your message…'
      : placeholder;

  return (
    <div
      className="aa-composer"
      data-recording={recording || undefined}
      data-transcribing={transcribing || undefined}
    >
      {/* Live branded waveform on the top border while recording. */}
      <VoiceWaveform analyserRef={voice.analyserRef} active={recording} />

      <textarea
        ref={textareaRef}
        className="aa-composer-textarea"
        rows={1}
        placeholder={textareaPlaceholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKey}
        readOnly={recording || transcribing}
        disabled={disabled || submitting}
        aria-label="Message Orbit"
      />

      <div className="aa-composer-actions">
        <div className="aa-composer-attaches">
          <button
            type="button"
            className="aa-composer-iconbtn"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Upload document"
            disabled={disabled || busy}
          >
            <Paperclip size={18} strokeWidth={2} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.pptx,.xlsx,.md,.txt,.csv,.zip,image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {/* Mic: idle → record · recording → stop (pulsing) · transcribing → spinner */}
          {transcribing ? (
            <span
              className="aa-composer-iconbtn aa-composer-iconbtn--transcribing"
              aria-label="Transcribing"
            >
              <Loader2 size={18} className="aa-spin" />
            </span>
          ) : (
            <button
              type="button"
              className={`aa-composer-iconbtn${recording ? ' aa-composer-iconbtn--recording' : ''}`}
              onClick={toggleVoice}
              aria-label={recording ? 'Stop recording' : 'Record voice message'}
              disabled={disabled || submitting}
            >
              {recording ? <Square size={15} strokeWidth={2.5} /> : <Mic size={18} strokeWidth={2} />}
            </button>
          )}

          {recording && <span className="aa-composer-mode aa-composer-mode--rec">recording</span>}
          {transcribing && <span className="aa-composer-mode">transcribing…</span>}
          {!recording && !transcribing && isUrl && (
            <span className="aa-composer-mode">
              <LinkIcon size={11} strokeWidth={2.5} /> scan site
            </span>
          )}
          {voice.error && !recording && !transcribing && (
            <span className="aa-composer-mode aa-composer-mode--err">{voice.error}</span>
          )}
        </div>

        <button
          type="button"
          className="aa-composer-send"
          onClick={submit}
          disabled={!text.trim() || busy || disabled}
          aria-label={isUrl ? 'Scan site' : 'Send'}
        >
          {submitting ? <Loader2 size={16} className="aa-spin" /> : <ArrowUp size={16} strokeWidth={2.5} />}
        </button>
      </div>
    </div>
  );
}

export default Composer;
